import { EncryptStorage } from "encrypt-storage";
import userController from "../controllers/userController";

const AUTH_STORAGE_KEY = "jwt";
const REMEMBERED_SIGN_IN_KEY = "remembered-sign-in";
const USER_DATA_STORAGE_KEY = "UserData";
const LEGACY_AUTH_STORAGE_KEYS = ["user-azalea", "Authorization"];

const encryptedStorage = new EncryptStorage("secret-key", {
  prefix: "@d2wn-auth",
});
let refreshJwtPromise = null;

const removeBearerPrefix = (token) =>
  typeof token === "string" ? token.replace(/^Bearer\s+/i, "").trim() : "";

const removeLegacyAuthStorage = () => {
  try {
    if (typeof localStorage === "undefined") return;

    LEGACY_AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
  } catch {
    // Ignore unavailable storage.
  }
};

const readEncrypted = (key) => {
  try {
    return encryptedStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeEncrypted = (key, value) => {
  try {
    encryptedStorage.setItem(key, value);
  } catch {
    // Ignore unavailable storage.
  }
};

const removeEncrypted = (key) => {
  try {
    encryptedStorage.removeItem(key);
  } catch {
    // Ignore unavailable storage.
  }
};

const getTokenFromStoredValue = (value) => {
  if (!value) return "";

  if (typeof value === "string") {
    const trimmedValue = value.trim();

    if (!trimmedValue) return "";

    try {
      const parsedValue = JSON.parse(trimmedValue);
      const parsedToken = getTokenFromStoredValue(parsedValue);

      if (parsedToken) return parsedToken;
    } catch {
      // Plain token strings are expected.
    }

    return removeBearerPrefix(trimmedValue);
  }

  if (typeof value !== "object") return "";

  return removeBearerPrefix(
    value.jwt ||
      value.token ||
      value.accessToken ||
      value.access_token ||
      value.authenticationToken ||
      value.idToken ||
      value.userJWT ||
      value.UserJWT ||
      value.JWT ||
      value.Authorization ||
      value.authorization ||
      "",
  );
};

/**
 * Reads a JWT from an API response body or Authorization header.
 * Example: const jwt = userService.getTokenFromResponse(loginResponse);
 */
const getTokenFromResponse = (response) => {
  const responseData =
    response?.data?.data ?? response?.data?.result ?? response?.data;
  const headerToken =
    response?.headers?.authorization || response?.headers?.Authorization;

  return (
    getTokenFromStoredValue(headerToken) ||
    getTokenFromStoredValue(responseData) ||
    ""
  );
};

/**
 * Reads the currently saved JWT from encrypted storage.
 * Example: const Authorization = userService.getJwt();
 */
const getJwt = () => {
  removeLegacyAuthStorage();

  return getTokenFromStoredValue(readEncrypted(AUTH_STORAGE_KEY));
};

/**
 * Saves the JWT in encrypted storage and returns the saved token.
 * Example: userService.saveJwt(userService.getTokenFromResponse(response));
 */
const saveJwt = (jwt) => {
  const token = getTokenFromStoredValue(jwt);

  removeLegacyAuthStorage();

  if (!token) {
    removeEncrypted(AUTH_STORAGE_KEY);
    return "";
  }

  writeEncrypted(AUTH_STORAGE_KEY, token);

  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("user-session-updated", {
        detail: { jwt: token },
      }),
    );
  }

  return token;
};

/**
 * Re-authenticates with the saved JWT, saves the refreshed JWT, and returns it.
 * Example: await userService.refreshJwt();
 */
const refreshJwt = async () => {
  if (refreshJwtPromise) return refreshJwtPromise;

  const jwt = getJwt();
  if (!jwt) return "";

  refreshJwtPromise = userController
    .reAuthenticate({ Authorization: jwt })
    .then((response) => {
      if (response?.isAxiosError || response?.response) {
        throw response;
      }

      const refreshedJwt = getTokenFromResponse(response);

      return refreshedJwt ? saveJwt(refreshedJwt) : jwt;
    })
    .finally(() => {
      refreshJwtPromise = null;
    });

  return refreshJwtPromise;
};

/**
 * Clears the saved JWT and UserData from encrypted storage.
 * Example: userService.logout();
 */
const logout = () => {
  removeEncrypted(AUTH_STORAGE_KEY);
  removeEncrypted(USER_DATA_STORAGE_KEY);
  removeLegacyAuthStorage();

  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("user-session-updated", {
        detail: { jwt: "" },
      }),
    );
  }
};

/**
 * Decodes and returns the payload object from the saved JWT.
 * Example: const payload = userService.getUserPayload();
 */
const getUserPayload = () => {
  try {
    const jwt = getJwt();
    const encodedPayload = jwt.split(".")[1];

    if (!encodedPayload) return null;

    const base64 = encodedPayload.replace(/-/g, "+").replace(/_/g, "/");
    const paddedBase64 = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      "=",
    );
    const decodedPayload = decodeURIComponent(
      atob(paddedBase64)
        .split("")
        .map(
          (character) =>
            `%${`00${character.charCodeAt(0).toString(16)}`.slice(-2)}`,
        )
        .join(""),
    );

    return JSON.parse(decodedPayload);
  } catch {
    return null;
  }
};

/**
 * Reads saved sign-in credentials used by the remember-me flow.
 * Example: const remembered = userService.getRememberedSignIn();
 */
const getRememberedSignIn = () => {
  const saved = readEncrypted(REMEMBERED_SIGN_IN_KEY);

  return saved && typeof saved === "object" ? saved : null;
};

/**
 * Saves remember-me credentials, or clears them if username/password is empty.
 * Example: userService.setRememberedSignIn({ username, password });
 */
const setRememberedSignIn = ({ username = "", password = "" } = {}) => {
  const trimmedUsername = String(username || "").trim();

  if (!trimmedUsername || !password) {
    removeEncrypted(REMEMBERED_SIGN_IN_KEY);
    return;
  }

  writeEncrypted(REMEMBERED_SIGN_IN_KEY, {
    username: trimmedUsername,
    password,
  });
};

/**
 * Clears remember-me credentials from encrypted storage.
 * Example: userService.clearRememberedSignIn();
 */
const clearRememberedSignIn = () => {
  removeEncrypted(REMEMBERED_SIGN_IN_KEY);
};

/**
 * Reads saved user session data such as UserID, Compid, BPID, and BType.
 * Example: const companyId = userService.getUserData().Compid;
 */
const getUserData = () => {
  const saved = readEncrypted(USER_DATA_STORAGE_KEY);

  return saved && typeof saved === "object" ? saved : {};
};

/**
 * Merges and saves user session data in one encrypted UserData object.
 * Example: userService.saveUserData({ UserID, Compid, BPID, BType });
 */
const saveUserData = (userData = {}) => {
  const nextUserData = {
    ...getUserData(),
    ...userData,
  };

  writeEncrypted(USER_DATA_STORAGE_KEY, nextUserData);

  return nextUserData;
};

/**
 * Clears the saved UserData object from encrypted storage.
 * Example: userService.clearUserData();
 */
const clearUserData = () => {
  removeEncrypted(USER_DATA_STORAGE_KEY);
};

/**
 * Legacy alias that returns the saved JWT payload.
 * Example: const user = userService.getUserObject();
 */
const getUserObject = () => getUserPayload();

/**
 * Legacy no-op kept for old callers; do not use for new code.
 * Example: userService.setUserObject();
 */
const setUserObject = () => null;

export const userService = {
  getJwt,
  saveJwt,
  refreshJwt,
  logout,
  getTokenFromResponse,
  getUserPayload,
  getUserData,
  saveUserData,
  clearUserData,
  getUserObject,
  setUserObject,
  getRememberedSignIn,
  setRememberedSignIn,
  clearRememberedSignIn,
};
